import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import { Loader2, Shield } from 'lucide-react';
import { useStockHistory, StockLearnStatus } from '../hooks/useStockHistory';
import { useAuth } from '../hooks/useAuth';
import { cn } from '../utils/cn';
import { STATUS_BG, STATUS_TEXT } from '../components/progress/BingoCard';
import { get } from '../utils/api';
import { normalizeStockOption } from '../utils/stockOptionFormatting';
import { getCollectible, CollectibleStock, getCollectibleLogoUrl } from '../config/collectiblesConfig';
import { EquipStockSheet } from '../components/progress/EquipStockSheet';

const PAGE_SIZE = 50;

type CellStatus = StockLearnStatus | 'empty';

interface StockCell {
  ticker: string;
  companyName: string;
  status: CellStatus;
  collectible?: CollectibleStock;
  logoUrl?: string | null;
}

const STATUS_META: { status: CellStatus; label: string; color: string }[] = [
  { status: 'learnt', label: 'Analyzed', color: 'bg-emerald-500' },
  { status: 'guessed', label: 'Guessed', color: 'bg-amber-400' },
  { status: 'empty', label: 'Yet to guess', color: 'bg-gray-300 dark:bg-gray-600' },
];

export const ProgressPage: React.FC = () => {
  const { user } = useAuth();
  const { stocks: playedStocks, isLoading: historyLoading } = useStockHistory(true);
  const [allStocks, setAllStocks] = useState<{ ticker: string; companyName: string }[]>([]);
  const [stocksLoading, setStocksLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Equip state
  const [selectedCell, setSelectedCell] = useState<StockCell | null>(null);
  const [equippedSymbol, setEquippedSymbol] = useState<string | null>(null);

  // Fetch equipped stock on mount
  useEffect(() => {
    let cancelled = false;
    const fetchEquipped = async () => {
      try {
        const res = await get<{ symbol: string | null; logoUrl: string | null }>(
          '/api/achievements/equipped-stock'
        );
        if (!cancelled && res.success && res.data) {
          setEquippedSymbol(res.data.symbol);
        }
      } catch {
        // Ignore
      }
    };
    if (user) fetchEquipped();
    return () => { cancelled = true; };
  }, [user]);

  // Fetch all stocks from the Household Name category (has every stock via allowAllStocksForGuessing)
  useEffect(() => {
    let cancelled = false;

    const fetchStocks = async () => {
      setStocksLoading(true);
      try {
        const response = await get<{ options: string[]; category: string; count: number }>(
          '/api/stocks/categories/popular/options'
        );
        if (!cancelled && response.success && response.data?.options) {
          const seen = new Set<string>();
          const result: { ticker: string; companyName: string }[] = [];
          for (const raw of response.data.options) {
            const normalized = normalizeStockOption(raw);
            if (normalized.ticker && !seen.has(normalized.ticker)) {
              seen.add(normalized.ticker);
              result.push({ ticker: normalized.ticker, companyName: normalized.company });
            }
          }
          setAllStocks(result);
        }
      } catch {
        // Failed to load
      } finally {
        if (!cancelled) setStocksLoading(false);
      }
    };

    fetchStocks();
    return () => { cancelled = true; };
  }, []);

  // Build status lookup from played stocks
  const statusMap = useMemo(() => {
    const map = new Map<string, StockLearnStatus>();
    for (const s of playedStocks) {
      const existing = map.get(s.stockSymbol);
      // Keep the "best" status: learnt > guessed
      if (!existing || (s.status === 'learnt' && existing === 'guessed')) {
        map.set(s.stockSymbol, s.status);
      }
    }
    return map;
  }, [playedStocks]);

  // Build logo URL lookup from played stock history
  const logoMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of playedStocks) {
      if (s.logoUrl && !map.has(s.stockSymbol)) {
        map.set(s.stockSymbol, s.logoUrl);
      }
    }
    return map;
  }, [playedStocks]);

  // Build collectibles lookup from played stock history
  const collectiblesMap = useMemo(() => {
    const map = new Map<string, CollectibleStock>();
    for (const stock of playedStocks) {
      const collectible = getCollectible(stock.stockSymbol, stock.gameDate);
      if (collectible) map.set(stock.stockSymbol, collectible);
    }
    return map;
  }, [playedStocks]);

  // Merge all stocks with statuses and sort (collectibles first)
  const cells = useMemo(() => {
    const order: Record<CellStatus, number> = { learnt: 0, guessed: 1, empty: 2 };
    return allStocks
      .map((s): StockCell => ({
        ticker: s.ticker,
        companyName: s.companyName,
        status: statusMap.get(s.ticker) || 'empty',
        collectible: collectiblesMap.get(s.ticker),
        logoUrl: logoMap.get(s.ticker),
      }))
      .sort((a, b) => {
        // Collectibles always first
        const aCollect = a.collectible ? 0 : 1;
        const bCollect = b.collectible ? 0 : 1;
        if (aCollect !== bCollect) return aCollect - bCollect;
        return order[a.status] - order[b.status] || a.ticker.localeCompare(b.ticker);
      });
  }, [allStocks, statusMap, collectiblesMap, logoMap]);

  const visibleCells = cells.slice(0, visibleCount);
  const hasMore = visibleCount < cells.length;

  const counts = useMemo(() => {
    const c = { learnt: 0, guessed: 0, empty: 0 };
    for (const cell of cells) c[cell.status]++;
    return c;
  }, [cells]);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasMore) {
        setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, cells.length));
      }
    },
    [hasMore, cells.length]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(handleIntersect, { rootMargin: '200px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleIntersect]);

  const handleCellClick = (cell: StockCell) => {
    if (cell.status === 'empty') return;
    setSelectedCell(cell);
  };

  const handleEquipChange = (equipped: { symbol: string; logoUrl: string } | null) => {
    setEquippedSymbol(equipped?.symbol ?? null);
  };

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const isLoading = historyLoading || stocksLoading;

  return (
    <>
    <Helmet>
      <title>Wallstreetle | Stock Progress Tracker</title>
      <meta
        name="description"
        content="Track your stock knowledge progress on Wallstreetle. See which companies you've analyzed, guessed, and still need to explore across all categories."
      />
      <link rel="canonical" href="https://wallstreetle.com/progress" />
      <meta property="og:title" content="Wallstreetle | Stock Progress Tracker" />
      <meta property="og:description" content="Track your stock knowledge progress. See which companies you've analyzed and explored across all categories on Wallstreetle." />
      <meta property="og:url" content="https://wallstreetle.com/progress" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://wallstreetle.com/assets/share-link-image.png" />
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:title" content="Wallstreetle | Stock Progress Tracker" />
      <meta property="twitter:description" content="Track your stock knowledge progress. See which companies you've analyzed and explored across all categories on Wallstreetle." />
      <meta property="twitter:image" content="https://wallstreetle.com/assets/share-link-image.png" />
    </Helmet>
    <div className="mx-auto max-w-md px-4 py-6 space-y-5">
      <div className="text-center space-y-1">
        <h1 className="text-2xl font-bold text-foreground">Progress Tracker</h1>
        <p className="text-sm text-muted-foreground">All stocks across every category</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : cells.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">
          No stocks found. Check back later!
        </p>
      ) : (
        <>
          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            {STATUS_META.map(({ status, label, color }) => (
              <div key={status} className="flex items-center gap-1.5">
                <span className={cn('h-3 w-3 rounded-full', color)} />
                <span className="text-muted-foreground">
                  {label} ({counts[status]})
                </span>
              </div>
            ))}
          </div>

          {/* Hint */}
          <p className="text-center text-xs text-muted-foreground">
            Tap a guessed stock to equip its logo on the leaderboard
          </p>

          {/* Bingo-style grid */}
          <div className="grid grid-cols-5 gap-1.5">
            {visibleCells.map((cell, i) => {
              const isWon = cell.status !== 'empty';
              const isCellEquipped = equippedSymbol === cell.ticker;
              return (
                <button
                  key={`${cell.ticker}-${i}`}
                  type="button"
                  disabled={!isWon}
                  onClick={() => handleCellClick(cell)}
                  className={cn(
                    'relative flex items-center justify-center rounded-md aspect-square min-h-[3rem]',
                    'font-mono text-xs font-semibold select-none',
                    'transition-colors overflow-hidden',
                    STATUS_BG[cell.status],
                    STATUS_TEXT[cell.status],
                    cell.collectible && 'ring-2 ring-yellow-400',
                    isCellEquipped && 'ring-2 ring-blue-500',
                    isWon && 'cursor-pointer active:scale-95 transition-transform'
                  )}
                  title={`${cell.companyName} (${cell.ticker})`}
                >
                  {cell.collectible ? (
                    <img
                      src={getCollectibleLogoUrl(cell.collectible)}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-contain p-1 opacity-50 pointer-events-none"
                    />
                  ) : cell.logoUrl ? (
                    <img
                      src={cell.logoUrl}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-contain p-1.5 opacity-40 pointer-events-none"
                    />
                  ) : null}
                  <span className="relative z-10">{cell.ticker}</span>
                  {isCellEquipped && (
                    <Shield className="absolute top-0.5 right-0.5 h-3 w-3 text-blue-500 z-10" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Sentinel for infinite scroll */}
          {hasMore && <div ref={sentinelRef} className="h-1" />}
        </>
      )}
    </div>

    {/* Equip bottom sheet */}
    {selectedCell && (
      <EquipStockSheet
        isOpen={!!selectedCell}
        onClose={() => setSelectedCell(null)}
        ticker={selectedCell.ticker}
        companyName={selectedCell.companyName}
        logoUrl={selectedCell.logoUrl ?? null}
        isEquipped={equippedSymbol === selectedCell.ticker}
        onEquipChange={handleEquipChange}
      />
    )}
    </>
  );
};
